import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { Inbox, Eye, MailOpen, Send, ExternalLink, Trash2, Phone, Mail } from "lucide-react";

const STATUS_STYLES = {
  nuovo: "bg-red-100 text-red-700 border-red-200",
  contattato: "bg-amber-100 text-amber-700 border-amber-200",
  chiuso: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function FunnelInbox() {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [promos, setPromos] = useState([]);
  const [summary, setSummary] = useState({ requests: 0, nuovi: 0, visits: 0, opens: 0, submits: 0 });

  const load = async () => {
    try {
      const [l, s] = await Promise.all([StatusApi.listFunnelLeads(), StatusApi.funnelSummary()]);
      setLeads(l);
      setSummary(s);
    } catch {}
    try { setPromos(await StatusApi.listPromos()); } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (open) load(); }, [open]);

  const setStatus = async (id, status) => {
    await StatusApi.updateFunnelLead(id, { status });
    setLeads((p) => p.map((x) => (x.id === id ? { ...x, status } : x)));
  };
  const remove = async (id) => {
    await StatusApi.deleteFunnelLead(id);
    setLeads((p) => p.filter((x) => x.id !== id));
    toast.success("Richiesta eliminata");
  };

  const landingUrl = `${window.location.origin}/presentazione`;
  const origin = window.location.origin;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="open-funnel-btn" className="h-9 relative">
          <Inbox className="h-4 w-4 md:mr-1.5" />
          <span className="hidden md:inline">Richieste</span>
          {summary.nuovi > 0 && (
            <span data-testid="funnel-badge"
              className="absolute -top-1.5 -right-1.5 h-5 min-w-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
              {summary.nuovi}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" /> Funnel — Richieste di contatto
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 shrink-0">
          <Stat icon={Eye} label="Visite landing" value={summary.visits} />
          <Stat icon={MailOpen} label="Aperture email" value={summary.opens} />
          <Stat icon={Send} label="Richieste" value={summary.requests} />
        </div>

        <div className="py-2 space-y-2 shrink-0">
          <LinkRow label="Presentazione" url={landingUrl} testid="landing-url" />
          {promos.length === 0 ? (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground shrink-0">Immobile:</span>
              <span className="text-muted-foreground/70">Nessun immobile pubblicato. Crea una campagna "Immobile" per generarne il link.</span>
            </div>
          ) : (
            promos.slice(0, 3).map((p) => (
              <LinkRow key={p.id} label={p.title ? `Immobile · ${p.title}` : "Immobile"}
                url={`${origin}/propostaimmobiliare/${p.id}`} testid={`immobile-url-${p.id}`} />
            ))
          )}
        </div>

        <div className="overflow-auto flex-1 -mx-1 px-1">
          {leads.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="funnel-empty">
              <Inbox className="h-10 w-10 mx-auto opacity-30 mb-3" />
              Nessuna richiesta ancora. Condividi il link della landing con i tuoi contatti.
            </div>
          ) : (
            <div className="space-y-2">
              {leads.map((l) => (
                <div key={l.id} data-testid={`funnel-lead-${l.id}`}
                  className="border border-border rounded-md p-3 hover:bg-muted/40 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                        {l.name || "—"}
                        {l.company && <span className="text-xs text-muted-foreground">· {l.company}</span>}
                        {l.interest && <span className="text-[11px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{l.interest}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                        {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone className="h-3 w-3" />{l.phone}</a>}
                        {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Mail className="h-3 w-3" />{l.email}</a>}
                        <span>{new Date(l.created_at).toLocaleString("it-IT")}</span>
                      </div>
                      {l.message && <p className="text-xs text-foreground mt-2 bg-muted/50 rounded p-2">{l.message}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                        <SelectTrigger data-testid={`funnel-status-${l.id}`} className={`h-8 text-xs w-32 border ${STATUS_STYLES[l.status] || ""}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="nuovo">nuovo</SelectItem>
                          <SelectItem value="contattato">contattato</SelectItem>
                          <SelectItem value="chiuso">chiuso</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => remove(l.id)} data-testid={`delete-funnel-${l.id}`}
                        className="text-muted-foreground hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-border p-3 text-center">
    <Icon className="h-4 w-4 mx-auto text-primary mb-1" />
    <div className="font-display font-black text-xl">{value}</div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
  </div>
);

const LinkRow = ({ label, url, testid }) => (
  <div className="flex items-center justify-between gap-2 text-xs">
    <span className="text-muted-foreground shrink-0">{label}:</span>
    <div className="flex items-center gap-2 min-w-0">
      <code className="truncate text-primary" data-testid={testid}>{url}</code>
      <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`open-${testid}`}
        className="text-primary shrink-0"><ExternalLink className="h-4 w-4" /></a>
      <button data-testid={`copy-${testid}`} className="text-muted-foreground hover:text-primary shrink-0"
        onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copiato"); }}>Copia</button>
    </div>
  </div>
);
